// Período: um número e uma unidade, escrito uma vez (componente 3.1).
//
// O módulo de compliance faz a mesma pergunta em cinco lugares — em quanto
// tempo o treinamento tem de acontecer, quanto tempo o certificado vale, em
// quanto tempo o alvará precisa ser renovado, de quanto em quanto tempo o
// equipamento é revisado, quanto o órgão demora para emitir. Aqui ficam as
// regras que todos compartilham.
//
// ─── Espelho do banco, e o banco é quem manda ────────────────────────
//
// As mesmas funções existem em SQL na migration 0045 — `periodo_em_dias`,
// `somar_periodo`, `periodo_texto`. Isto aqui é retorno imediato para quem está
// digitando; quem grava a coluna gerada e calcula o vencimento é o banco. Se as
// duas discordarem, a do banco é a que vale — como já acontece com
// `lib/formulario.ts` e `_shared/formulario.ts`.
//
// Elas precisam continuar concordando, então a conversão em dias usa aqui a
// mesma média fixa que a versão IMMUTABLE do Postgres usa: mês = 30, ano = 365.

export const UNIDADES_DE_PERIODO = ['dias', 'meses', 'anos'] as const;

export type UnidadeDePeriodo = (typeof UNIDADES_DE_PERIODO)[number];

export interface Periodo {
  valor: number;
  unidade: UnidadeDePeriodo;
}

/** Um Período pela metade — o estado em que um formulário vive enquanto é preenchido. */
export interface PeriodoParcial {
  valor: number | null;
  unidade: UnidadeDePeriodo | null;
}

const DIAS_POR_UNIDADE: Record<UnidadeDePeriodo, number> = {
  dias: 1,
  meses: 30,
  anos: 365,
};

const SINGULAR: Record<UnidadeDePeriodo, string> = {
  dias: 'dia',
  meses: 'mês',
  anos: 'ano',
};

export function unidadeValida(valor: unknown): valor is UnidadeDePeriodo {
  return typeof valor === 'string' && (UNIDADES_DE_PERIODO as readonly string[]).includes(valor);
}

/**
 * O Período está completo?
 *
 * "Vazio" é um estado legítimo: item sem manutenção recorrente, alvará que não
 * vence. O que a constraint do banco recusa é a metade — valor sem unidade, ou
 * unidade sem valor.
 */
export function periodoCompleto(p: PeriodoParcial | null | undefined): p is Periodo {
  return !!p && typeof p.valor === 'number' && p.valor > 0 && unidadeValida(p.unidade);
}

export function periodoVazio(p: PeriodoParcial | null | undefined): boolean {
  return !p || ((p.valor === null || p.valor === 0) && p.unidade === null);
}

/**
 * Duração aproximada em dias, para comparar e ordenar.
 *
 * **Não use para calcular vencimento.** "1 ano a partir de 29/02" não é
 * "+365 dias". Para data existe `somarPeriodo()`.
 */
export function periodoEmDias(p: PeriodoParcial | null | undefined): number | null {
  if (!periodoCompleto(p)) return null;
  return p.valor * DIAS_POR_UNIDADE[p.unidade];
}

/** Data + Período, com aritmética de calendário. É esta que calcula vencimento. */
export function somarPeriodo(
  base: Date | null | undefined,
  p: PeriodoParcial | null | undefined,
): Date | null {
  if (!base || !periodoCompleto(p)) return null;

  const data = new Date(base.getFullYear(), base.getMonth(), base.getDate());

  if (p.unidade === 'dias') {
    data.setDate(data.getDate() + p.valor);
    return data;
  }

  const meses = p.unidade === 'anos' ? p.valor * 12 : p.valor;
  const diaOriginal = data.getDate();
  data.setMonth(data.getMonth() + meses);

  // 31/01 + 1 mês vira 03/03 no JavaScript, porque fevereiro não tem 31. O
  // Postgres devolve 28/02, e um vencimento que anda três dias entre a tela e o
  // banco é o tipo de divergência que ninguém procura. Volta para o último dia
  // do mês pretendido.
  if (data.getDate() !== diaOriginal) {
    data.setDate(0);
  }

  return data;
}

/** Como o Período se lê. O singular importa numa tela de conformidade. */
export function periodoTexto(p: PeriodoParcial | null | undefined): string {
  if (!periodoCompleto(p)) return '—';
  return p.valor === 1 ? `1 ${SINGULAR[p.unidade]}` : `${p.valor} ${p.unidade}`;
}

/**
 * Dias para o Período mais natural.
 *
 * 730 vira "2 anos", e não "730 dias": quem preencheu pensou em anos, e devolver
 * dias na tela faz a pessoa reescrever o que já estava certo. Espelha
 * `dias_em_periodo()` da migration 0045.
 */
export function diasEmPeriodo(dias: number | null | undefined): PeriodoParcial {
  if (dias === null || dias === undefined || dias === 0) {
    return { valor: null, unidade: null };
  }
  if (dias % 365 === 0) return { valor: dias / 365, unidade: 'anos' };
  if (dias % 30 === 0) return { valor: dias / 30, unidade: 'meses' };
  return { valor: dias, unidade: 'dias' };
}

/**
 * Lê o par de colunas que o banco usa.
 *
 * As tabelas guardam `<prefixo>_valor` e `<prefixo>_unidade`. Esta função é a
 * ponte entre esse formato e o objeto que os componentes manipulam, para o
 * `snake_case` do banco não vazar por toda a interface.
 */
export function periodoDeColunas(
  linha: Record<string, unknown> | null | undefined,
  prefixo: string,
): PeriodoParcial {
  const valor = linha?.[`${prefixo}_valor`];
  const unidade = linha?.[`${prefixo}_unidade`];
  return {
    valor:
      typeof valor === 'number'
        ? valor
        : valor === null || valor === undefined
          ? null
          : Number(valor),
    unidade: unidadeValida(unidade) ? unidade : null,
  };
}

/** O caminho de volta: o objeto vira o par de colunas que o banco espera. */
export function periodoParaColunas(
  p: PeriodoParcial | null | undefined,
  prefixo: string,
): Record<string, number | string | null> {
  const completo = periodoCompleto(p);
  return {
    [`${prefixo}_valor`]: completo ? p.valor : null,
    [`${prefixo}_unidade`]: completo ? p.unidade : null,
  };
}

export const MENSAGEM_PERIODO = {
  incompleto: 'Informe o número e a unidade, ou deixe os dois em branco.',
  naoPositivo: 'O período precisa ser maior que zero.',
} as const;

/**
 * Valida o Período como o banco valida.
 *
 * Devolve `null` quando está certo — vazio ou completo — e a mensagem quando
 * está pela metade, que é exatamente o que a constraint recusa.
 */
export function validarPeriodo(p: PeriodoParcial | null | undefined): string | null {
  if (periodoVazio(p)) return null;
  if (p && typeof p.valor === 'number' && p.valor <= 0) return MENSAGEM_PERIODO.naoPositivo;
  if (!periodoCompleto(p)) return MENSAGEM_PERIODO.incompleto;
  return null;
}
